// v2.1 BOSS BLESSINGS — mid-run boons offered after each mid-boss victory (floors 10, 15).
// v2.3 RARITY TIERS: common (green, weight 60), rare (blue, weight 30), legendary (gold, weight 10).
// Each blessing is a permanent run-long modifier chosen from 3 rarity-weighted offers.
// Applied via player._blessings[] array; engine + handleCombatEnd read the flags.

export const RARITY_COLORS = {
  common: '#a0ff2e',    // neon green
  rare: '#00e5ff',      // cyan
  legendary: '#ffd700', // gold
};
export const RARITY_WEIGHTS = { common: 60, rare: 30, legendary: 10 };

export const BLESSINGS = {
  // --- COMMON (balanced, always useful) ---
  bless_block: {
    id: 'bless_block',
    name: 'FORTIFY',
    desc: 'Start every combat with 10 BLOCK.',
    art: '🛡',
    rarity: 'common',
    apply: (mods) => { mods.blessStartBlock = (mods.blessStartBlock || 0) + 10; },
  },
  bless_regen: {
    id: 'bless_regen',
    name: 'HEALING FACTOR',
    desc: 'Heal 5 HP after every combat victory.',
    art: '🩹',
    rarity: 'common',
    apply: (mods) => { mods.blessPostCombatHeal = (mods.blessPostCombatHeal || 0) + 5; },
  },
  bless_firstfree: {
    id: 'bless_firstfree',
    name: 'FREE FIRST',
    desc: 'The first card you play each turn costs 0.',
    art: '🎫',
    rarity: 'common',
    apply: (mods) => { mods.blessFirstCardFree = true; },
  },
  bless_reward: {
    id: 'bless_reward',
    name: 'EXTRA LOOT',
    desc: '+1 card in every card reward.',
    art: '🎁',
    rarity: 'common',
    apply: (mods) => { mods.blessExtraRewardCards = (mods.blessExtraRewardCards || 0) + 1; },
  },
  // --- RARE (impactful stat swings) ---
  bless_strength: {
    id: 'bless_strength',
    name: 'SIGMA BLESSING',
    desc: '+2 STRENGTH at the start of every combat.',
    art: '💪',
    rarity: 'rare',
    apply: (mods) => { mods.blessStartStr = (mods.blessStartStr || 0) + 2; },
  },
  bless_energy: {
    id: 'bless_energy',
    name: 'CAFFEINE PACT',
    desc: '+1 max ENERGY every turn.',
    art: '⚡',
    rarity: 'rare',
    apply: (mods) => { mods.blessEnergyDelta = (mods.blessEnergyDelta || 0) + 1; },
  },
  bless_draw: {
    id: 'bless_draw',
    name: 'ALGORITHM BOON',
    desc: 'Draw +1 card each turn.',
    art: '🃏',
    rarity: 'rare',
    apply: (mods) => { mods.blessDrawDelta = (mods.blessDrawDelta || 0) + 1; },
  },
  bless_maxhp: {
    id: 'bless_maxhp',
    name: 'HYPERTROPHY',
    desc: '+15 MAX HP right now (also heals 15).',
    art: '❤️‍🔥',
    rarity: 'rare',
    apply: (mods, player) => {
      if (player) {
        player.maxHp += 15;
        player.hp = Math.min(player.maxHp, player.hp + 15);
      }
    },
  },
  bless_gold: {
    id: 'bless_gold',
    name: 'CAPITAL GAINS',
    desc: '+10 GOLD on every kill.',
    art: '💰',
    rarity: 'rare',
    apply: (mods) => { mods.blessGoldOnKill = (mods.blessGoldOnKill || 0) + 10; },
  },
  bless_synergyx: {
    id: 'bless_synergyx',
    name: 'META SYNERGY',
    desc: 'Tag synergies fire at -1 threshold (min 2).',
    art: '🌀',
    rarity: 'rare',
    apply: (mods) => { mods.blessSynergyReduce = (mods.blessSynergyReduce || 0) + 1; },
  },
  // --- LEGENDARY (run-defining game-changers) ---
  bless_doublestr: {
    id: 'bless_doublestr',
    name: 'DOUBLE ARC',
    desc: 'DOUBLE all STRENGTH bonuses (base + gained).',
    art: '🔥',
    rarity: 'legendary',
    apply: (mods) => { mods.blessDoubleStr = true; },
  },
  bless_onefree: {
    id: 'bless_onefree',
    name: 'BASIC INSTINCT',
    desc: 'All 1-cost cards cost 0.',
    art: '🌌',
    rarity: 'legendary',
    apply: (mods) => { mods.blessOneCostFree = true; },
  },
  bless_drawonkill: {
    id: 'bless_drawonkill',
    name: 'FEEDING FRENZY',
    desc: 'Draw a fresh hand whenever you kill an enemy.',
    art: '🩸',
    rarity: 'legendary',
    apply: (mods) => { mods.blessDrawOnKill = true; },
  },
  bless_reflect: {
    id: 'bless_reflect',
    name: 'MIRROR AURA',
    desc: 'Reflect 30% of damage taken back to attacker.',
    art: '💎',
    rarity: 'legendary',
    apply: (mods) => { mods.blessReflectPct = (mods.blessReflectPct || 0) + 0.30; },
  },
  bless_ascension: {
    id: 'bless_ascension',
    name: 'ASCENSION CROWN',
    desc: '+5 MAX HP AND +1 STR at the start of EACH combat (permanent, stacking).',
    art: '👑',
    rarity: 'legendary',
    apply: (mods) => {
      mods.blessAscensionCrown = (mods.blessAscensionCrown || 0) + 1;
    },
  },
};

// Rarity-weighted picker: build a pool with duplicated entries per rarity weight.
function weightedRoll(rng, offers) {
  const pool = [];
  for (const o of offers) {
    const w = RARITY_WEIGHTS[o.rarity] || 10;
    for (let i = 0; i < w; i++) pool.push(o);
  }
  return pool[Math.floor(rng() * pool.length)];
}

// Pick 3 random blessings the player doesn't already own, weighted by rarity.
export function rollBlessingOffers(rng, ownedIds = []) {
  const owned = new Set(ownedIds);
  const available = Object.values(BLESSINGS).filter(b => !owned.has(b.id));
  const picks = [];
  const remaining = [...available];
  while (picks.length < 3 && remaining.length > 0) {
    const chosen = weightedRoll(rng, remaining);
    picks.push(chosen);
    const idx = remaining.findIndex(r => r.id === chosen.id);
    if (idx >= 0) remaining.splice(idx, 1);
  }
  return picks;
}

